import {
  CompanyTabElement,
  CompanyTabTitle,
} from "../../models/CompanyprofileModels";
import companyDescription from "../../assets/svg/company_description.svg";
import companyDescriptionActive from "../../assets/svg/company_description_active.svg";
import companyData from "../../assets/svg/company_data.svg";
import companyDataActive from "../../assets/svg/company_data_active.svg";
import netZeroAssessment from "../../assets/svg/net_zero_assessment.svg";
import netZeroAssessmentActive from "../../assets/svg/net_zero_assessment_active.svg";
import environmentalProfile from "../../assets/svg/environmental_profile.svg";
import environmentalProfileActive from "../../assets/svg/environmental_profile_active.svg";
import {
  Alignment,
  Engagement,
  EngagementColumnType,
  EngagementRecord,
  EngagementType,
  Milestone,
  VotingColumnType,
} from "../../models/stewardshipModels";

import { ColumnType, TableCellType } from "../../models/tableModels";
import { MilestoneKey } from "../../enums/Status";

export const companyTabElements: CompanyTabElement[] = [
  {
    title: CompanyTabTitle.CompanyDescription,
    icon: companyDescription,
    activeIcon: companyDescriptionActive,
    isHovered: false,
    isActive: false,
    isLocked: false,
  },
  {
    title: CompanyTabTitle.CompanyData,
    icon: companyData,
    activeIcon: companyDataActive,
    isHovered: false,
    isActive: false,
    isLocked: false,
  },
  {
    title: CompanyTabTitle.NetZeroAssessment,
    icon: netZeroAssessment,
    activeIcon: netZeroAssessmentActive,
    isHovered: false,
    isActive: false,
    isLocked: false,
  },
  {
    title: CompanyTabTitle.EnvironmentalProfile,
    icon: environmentalProfile,
    activeIcon: environmentalProfileActive,
    isHovered: false,
    isActive: false,
    isLocked: false,
  },
];

export const defaultCompanyDescription =
  "This company specializes in offering a wide range of products and services, tailored to meet diverse customer needs. With a commitment to innovation and quality, they focus on delivering exceptional value. Their customer-centric approach ensures consistent satisfaction, making them a trusted leader in their industry.";

export const MILESTONE_DESCRIPTIONS: { [key in MilestoneKey]: string } = {
  [MilestoneKey.M1]: "Raising concerns",
  [MilestoneKey.M2]: "Acknowledging concerns",
  [MilestoneKey.M3]: "Resolution planning",
  [MilestoneKey.M4]: "Implementing",
  [MilestoneKey.CI]: "Corporate Issue",
};

export const MILESTONE_TYPES: Milestone[] = [
  { displayValue: `M1: ${MILESTONE_DESCRIPTIONS.M1}`, key: MilestoneKey.M1 },
  { displayValue: `M2: ${MILESTONE_DESCRIPTIONS.M2}`, key: MilestoneKey.M2 },
  { displayValue: `M3: ${MILESTONE_DESCRIPTIONS.M3}`, key: MilestoneKey.M3 },
  { displayValue: `M4: ${MILESTONE_DESCRIPTIONS.M4}`, key: MilestoneKey.M4 },
  { displayValue: `CI: ${MILESTONE_DESCRIPTIONS.CI}`, key: MilestoneKey.CI },
];
export const ENGAGEMENT_TYPES: Engagement[] = [
  {
    displayValue: EngagementType.StrategyRiskCommunication,
    key: EngagementType.StrategyRiskCommunication,
  },
  { displayValue: EngagementType.Governance, key: EngagementType.Governance },
  { displayValue: EngagementType.Social, key: EngagementType.Social },
  {
    displayValue: EngagementType.Environmental,
    key: EngagementType.Environmental,
  },
];

export const ENGAGEMENT_BASE_COLUMNS: EngagementColumnType[] = [
  {
    title: "Engagement",
    key: "engagement_id",
    dataIndex: "engagement_title",
    fixed: "left",
  },
  {
    title: "Theme",
    key: "theme",
    dataIndex: "theme",
    hasFilters: true,
  },
  {
    title: "Sub-Theme",
    key: "sub_theme",
    dataIndex: "sub_theme",
  },
  {
    title: "Manager",
    key: "manager_id",
    dataIndex: "manager_name",
    hasFilters: true,
  },
  {
    title: "Milestone",
    key: "milestone_id",
    dataIndex: "milestone_description",
    hasFilters: true,
  },
  {
    title: "Engagement Type",
    key: "engagement_type_id",
    dataIndex: "engagement_type_name",
    hasFilters: true,
  },
  {
    title: "Last Action Date",
    key: "last_action_date",
    dataIndex: "last_action_date",
  },
  {
    key: "flagsDropdown",
    title: "Flag",
    dataIndex: "flagsDropdown",
    fixed: "right",
  },
];

export const ENGAGEMENT_COLUMN_TYPES: ColumnType[] = [
  { type: TableCellType.LINK, columnKey: "engagement_id" },
  { type: TableCellType.DROPDOWN, columnKey: "flagsDropdown" },
  { type: TableCellType.AVATAR, columnKey: "milestone_id" },
  { type: TableCellType.AVATAR, columnKey: "manager_id" },
];

export const ALIGNMENT_FILTERS: { text: string; value: Alignment }[] = [
  { text: "Misaligned Votes", value: Alignment.Misaligned },
  { text: "Aligned Votes", value: Alignment.Aligned },
];

export const VOTING_BASE_COLUMNS: VotingColumnType[] = [
  {
    title: "Proposal",
    key: "proposalId",
    dataIndex: "proposalDescription",
    fixed: "left",
  },
  {
    title: "Proposal Type",
    key: "proposalType",
    dataIndex: "proposalType",
    hasFilters: true,
  },
  {
    title: "Management Recommendation",
    key: "managementRecommendation",
    dataIndex: "managementRecommendation",
  },
];

export const VOTING_COLUMN_TYPES: ColumnType[] = [
  { type: TableCellType.DROPDOWN, columnKey: "flagsDropdown" },
  { type: TableCellType.TAG, columnKey: "managementRecommendation" },
];

export const MILESTONES_START_COLUMN_MAP: {
  [key in MilestoneKey]: keyof EngagementRecord;
} = {
  [MilestoneKey.M1]: "m1_start",
  [MilestoneKey.M2]: "m2_start",
  [MilestoneKey.M3]: "m3_start",
  [MilestoneKey.M4]: "m4_start",
  [MilestoneKey.CI]: "engagement_start",
};

export const MILESTONES_STATUS: {
  [key in MilestoneKey]: keyof EngagementRecord;
} = {
  [MilestoneKey.M1]: "m1_status",
  [MilestoneKey.M2]: "m2_status",
  [MilestoneKey.M3]: "m3_status",
  [MilestoneKey.M4]: "m4_status",
  [MilestoneKey.CI]: "engagement_start",
};

export const MILESTONES_ACTION_COLUMN_MAP: {
  [key in MilestoneKey]: keyof EngagementRecord;
} = {
  [MilestoneKey.M1]: "m1_action",
  [MilestoneKey.M2]: "m2_action",
  [MilestoneKey.M3]: "m3_action",
  [MilestoneKey.M4]: "m4_action",
  [MilestoneKey.CI]: "engagement_obj_desc",
};
